import styled, { css } from "styled-components";
import Hero from "src/components/Hero/Hero";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import Button from "src/components/Button/Button";
import EventFinished from "src/components/EventFinished";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import { FaCalendarAlt, FaEnvelope } from "react-icons/fa";

const ChineseNewYear2026 = () => {

  const { t, i18n } = useTranslation();
  const bgImage = useResponsiveImageUrl("promotions/cny-2026/CNY-bg-05.jpg");

  return (
    <Div bgImage={bgImage}>
      <Hero
        image={`promotions/cny-2026/CNY-logo_v2_${i18n.language}.png`}
      />
      <EventFinished
        scheduleKeyName="chineseNewYear2026"
        eventType="promotion"
      />

      <MainContentDiv>
        <container-div>
          <HeaderBanner
            bgImage={bgImage}
          >
            <div className="inner">
              <ReactMarkdown>
                {t("chineseNewYear2026.banner.text")}
              </ReactMarkdown>
            </div>
          </HeaderBanner>
          <IntroContainer className="padded">
            <Row className="g-5 d-reverse mb-4">
              <Col xs={12} md={6}>
                <ResponsiveImage
                  className="radius-mask"
                  src="promotions/cny-2025/promo-code/PG-Building.jpg"
                  alt={t("chineseNewYear2026.images.pgBuildingAlt")}
                />
              </Col>
              <Col xs={12} md={6}>
                <SubTitleSection
                  subTitle={t("chineseNewYear2026.intro.title")}
                  text={t("chineseNewYear2026.intro.text")}
                  borderColor="#c54132"
                  dark={false}
                  className="celebrate py-0"
                />
              </Col>
            </Row>

            <Row className="g-4 mb-0">
              <Col xs={12} md={6}>
                <ResponsiveImage
                  className="radius-mask"
                  src="promotions/cny-2026/Lion-Dance-img-2026.jpg"
                  alt={t("chineseNewYear2026.images.lionDanceAlt")}
                />
              </Col>
              <Col xs={12} md={6}>
                <LionDanceSection>
                  <div className="lion-header">
                    <ResponsiveImage
                      src="promotions/cny-2026/lion-head.png"
                      alt="Lion Head"
                      className="lion-icon"
                    />
                    <h2 className="lion-title">
                      {t("chineseNewYear2026.lionDance.title")}
                    </h2>
                  </div>
                  <p className="lion-description">
                    {t("chineseNewYear2026.lionDance.text")}
                  </p>
                </LionDanceSection>
                <Announcement>
                  <ReactMarkdown>
                    {t("chineseNewYear2026.lionDance.dates")}
                  </ReactMarkdown>
                  <ReactMarkdown>
                    {t("chineseNewYear2026.lionDance.times")}
                  </ReactMarkdown>
                </Announcement>
                <p>{t("chineseNewYear2026.lionDance.details")}</p>
              </Col>
            </Row>

            <Divider className="featured-games-divider">
              <ResponsiveImage src="promotions/cny-2025/promo-code/Divider.png" />
            </Divider>
          </IntroContainer>

          <PlainContainer className="padded extra-top featured-games">
            <SubTitleSection
              subTitle={t("chineseNewYear2026.featuredGames.title")}
              text={t("chineseNewYear2026.featuredGames.text")}
              className="text-center"
              dark={false}
            />
            <div className="text-center">
              <Button
                label={t("chineseNewYear2026.featuredGames.cta")}
                href={t("routes.chineseNewYear2026Games")}
                width="xwide"
                bgColor="#c40000"
                color="#ffffff"
                hoverBgColor="#b10000"
                hoverColor="#ffffff"
                className="featured-games-cta"
              />
            </div>
            <Divider>
              <ResponsiveImage src="promotions/cny-2025/promo-code/Divider.png" />
            </Divider>
          </PlainContainer>

          <PatternWrap
            bgImage={useResponsiveImageUrl(
              "promotions/cny-2025/promo-code/BG_Pattern_03.jpg",
            )}
          >
            <PlainContainer className="padded extra-top gala-section">
              <SubTitleSection
                subTitle={t("chineseNewYear2026.gala.title")}
                className="cny-special-title cny-gala-title text-center"
                dark={false}
              />
              <Announcement className="gala-announcement">
                <p>
                  <FaCalendarAlt className="icon" /> {t("chineseNewYear2026.gala.date")}
                </p>
                <p>
                  <FaEnvelope className="icon" /> {t("chineseNewYear2026.gala.emailLabel")} <a href={`mailto:${t("chineseNewYear2026.gala.email")}`}>{t("chineseNewYear2026.gala.email")}</a>
                </p>
              </Announcement>
              <ReactMarkdown className="text-center">
                {t("chineseNewYear2026.gala.text")}
              </ReactMarkdown>
            </PlainContainer>

            <PlainContainer className="padded extra-top">
              <CelebrationContainer
                bgImage={useResponsiveImageUrl(
                  "promotions/cny-2025/promo-code/BG_Pattern_02.png",
                )}
              >
                <h2 className="cny-gala-title cny-gala-title--sm">
                  {t("chineseNewYear2026.celebrationIncludes.title")}
                </h2>
                <CelebrationSection>
                  <CelebrationItem>
                    <ItemTitle>
                      {t("chineseNewYear2026.celebrationIncludes.buffet.title")}
                    </ItemTitle>
                    <p>
                      {t("chineseNewYear2026.celebrationIncludes.buffet.text")}
                    </p>
                  </CelebrationItem>
                  <CelebrationItem>
                    <ItemTitle>
                      {t(
                        "chineseNewYear2026.celebrationIncludes.performances.title",
                      )}
                    </ItemTitle>
                    <p>
                      {t(
                        "chineseNewYear2026.celebrationIncludes.performances.intro",
                      )}
                    </p>
                    <ul>
                      {t(
                        "chineseNewYear2026.celebrationIncludes.performances.items",
                        { returnObjects: true },
                      ).map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </CelebrationItem>
                </CelebrationSection>
              </CelebrationContainer>
            </PlainContainer>

            <PlainContainer className="padded extra-top extra-bottom">
              <Announcement
                bgImage={bgImage}
                className="text-center prize-draw red-envelopes"
              >
                <SubTitleSection
                  subTitle={t("chineseNewYear2026.luckyDraw.title")}
                  text={t("chineseNewYear2026.luckyDraw.text")}
                  className="cny-special-title"
                />
                <Row xs={2} className="prize-types">
                  <Col>
                    <ReactMarkdown>
                      {t("chineseNewYear2026.luckyDraw.prize2")}
                    </ReactMarkdown>
                  </Col>
                  <Col>
                    <ReactMarkdown>
                      {t("chineseNewYear2026.luckyDraw.prize1")}
                    </ReactMarkdown>
                  </Col>
                </Row>
                <ResponsiveImage src="promotions/cny-2025/promo-code/Coin.png" />
              </Announcement>
              <ReactMarkdown className="text-center">
                {t("chineseNewYear2026.luckyDraw.note")}
              </ReactMarkdown>
            </PlainContainer>
          </PatternWrap>

          <PlainContainer className="padded extra-top closing-container">
            <PromoSubTitleSection
              subTitle={t("chineseNewYear2026.closing.title")}
              text={t("chineseNewYear2026.closing.text")}
              className="cny-special-title text-center closing-section"
            />
          </PlainContainer>
        </container-div>
      </MainContentDiv>
    </Div>
  );
};

const PromoSubTitleSection = ({ subTitle, text, className }) => {
  const classes = ["subtitle-section", className].filter(Boolean).join(" ");
  const formattedTitle = subTitle ? subTitle.replace(/\n/g, "  \n") : "";

  return (
    <div className={classes}>
      {subTitle && (
        <ReactMarkdown
          components={{ p: ({ node, ...props }) => <h2 {...props} /> }}
        >
          {formattedTitle}
        </ReactMarkdown>
      )}
      {text && <ReactMarkdown>{text}</ReactMarkdown>}
    </div>
  );
};

const goldGradientTextOutline = css`
  background: linear-gradient(
    0deg,
    rgba(209, 148, 72, 1) 0%,
    rgba(248, 226, 170, 1) 100%
  );
  background-clip: text;
  -webkit-text-stroke: 8px transparent;
`;

const Div = styled.div`
  width: 100%;
  height: auto;
  background-color: #d16656;
  background-image: ${(props) => `url(${props.bgImage})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;

  .hero_text_container {
    display: none;
  }
  p {
    font-size: 1rem;
    line-height: 1.6;
  }
  .title-section {
    p {
      padding: 0;
    }
  }
  .celebrate {
    h2 {
      text-transform: uppercase;
      font-size: 2.5rem;
      @media (min-width: 768px) {
        padding-right: 30px;
        line-height: 1.2;
      }
    }
  }
  h1,
  h2,
  h3 {
    :before {
      display: none !important;
    }
  }
  .cny-special-title {
    margin-bottom: 48px;
    h2 {
      font-size: 2rem;
      font-weight: normal;
      &:after {
        display: none;
      }
    }
  }
  .cny-gala-title {
    margin-bottom: 0;
    font-family: "Arial Black", sans-serif;
    font-weight: 900;
    font-size: 35px;
    text-transform: uppercase;
    text-align: center;
    line-height: 1.1;
    letter-spacing: -1px;
    background: #ffffff;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    -webkit-text-stroke: 1.5px #c40000;
    filter: drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.3));
  }
  .cny-gala-title h2 {
    font-family: "Arial Black", sans-serif;
    font-weight: 900;
    font-size: 50px;
    text-transform: uppercase;
    text-align: center;
    line-height: 1.1;
    letter-spacing: -1px;
    background: #ffffff;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    -webkit-text-stroke: 1.5px #c40000;
    filter: drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.3));
  }
  .cny-gala-title--sm {
    font-size: 35px;
    margin-bottom: 1rem;
  }
  .cny-gala-title--sm h2 {
    font-size: 35px;
  }
  .featured-games {
    text-align: center;
    padding-top: 2.5rem;
    padding-bottom: 2.5rem;
  }
  .featured-games .subtitle-section h2 {
    font-family: 'Philosopher', serif;
    font-size: 2rem;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #3a2b1a;
    margin-bottom: 2rem;
  }
  .featured-games .subtitle-section h2:before,
  .featured-games .subtitle-section h2:after {
    display: none;
  }
  .featured-games .subtitle-section p {
    max-width: 780px;
    margin: 0.35rem auto;
    color: #3a2b1a;
  }
  .featured-games .featured-games-cta {
    margin: 1rem auto 1.6rem;
    border-top: 2px solid #f2d28b;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.2);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 0;
  }
  .featured-games .featured-games-divider {
    margin-top: 0.5rem;
  }
  @media (max-width: 768px) {
    .gala-section {
      padding-top: 4rem !important;
    }
    .featured-games {
      padding-top: 4rem !important;
      padding-bottom: 2rem;
    }
    .cny-gala-title {
      font-size: 28px;
      letter-spacing: -0.5px;
    }
    .cny-gala-title h2 {
      font-size: 32px;
      letter-spacing: -0.5px;
    }
    .cny-gala-title--sm,
    .cny-gala-title--sm h2 {
      font-size: 26px;
    }
    .featured-games .subtitle-section h2 {
      font-size: 1.5rem;
      letter-spacing: 0.5px;
    }
    .featured-games .subtitle-section p {
      font-size: 0.95rem;
      line-height: 1.5;
    }
    .featured-games .featured-games-divider {
      margin-top: 1rem;
    }
    p {
      font-size: 0.95rem;
      line-height: 1.5;
    }
  }
  .closing-section {
    h2 {
      font-family: "Arial Black", "Gadget", sans-serif;
      font-weight: 900;
      font-size: 42px;
      line-height: 1.1;
      text-align: center;
      text-transform: uppercase;
      color: #1b1b1b;
      letter-spacing: 0.5px;
      margin-bottom: 22px;
      text-shadow: 3px 3px 0 rgba(0, 0, 0, 0.25);
    }
    p {
      font-family: Arial, sans-serif;
      color: #2a2723;
      max-width: 900px;
      margin: 0 auto;
      font-weight: 400;
      text-align: center;
    }
    p + p {
      margin-top: 25px;
    }
  }
  .closing-container {
    background-color: rgb(222, 188, 139);
    padding: 56px 24px 48px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  @media (min-width: 768px) {
    .d-reverse {
      flex-direction: row-reverse;
    }
  }
  .padded {
    padding: 1em 1em 0;
    @media (min-width: 768px) {
      padding: 2em 2em 0;
    }
    @media (min-width: 991px) {
      padding: 3em 3em 0;
    }
    &.extra-top {
      padding-top: 3em 0 !important;
    }
    &.extra-bottom {
      padding-bottom: 3em !important;
    }
  }
`;

const LionDanceSection = styled.div`
  margin-bottom: 2rem;

  .lion-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .lion-icon {
    width: 60px;
    height: auto;
    flex-shrink: 0;

    @media (max-width: 768px) {
      width: 60px;
    }
  }

  .lion-title {
    font-family: "Philosopher", serif;
    font-size: 2rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #23201c;
    margin: 0;
    line-height: 1.2;
    flex: 1;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  .lion-description {
    font-size: 1rem;
    line-height: 1.6;
    color: #23201c;
    margin: 0 0 1rem 0;

    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  }
`;

const Announcement = styled.div`
  background-color: #bd3f30;
  background-image: ${(props) => `url(${props.bgImage})`};
  border-radius: 30px;
  color: #fff;
  padding: 1rem;
  margin: 1rem 0;
  p {
    padding: 0;
    margin: 0;
  }
  &.red-envelopes {
    color: black;
    padding: 1rem 1.5rem;
    .subtitle-section {
      padding-top: 0;
    }
    h2 {
      font-size: 4rem;
    }
    img {
      padding: 1rem;
      max-width: 227px;
    }
    .col-12 {
      flex-direction: column;
      justify-content: center;
      display: flex;
      align-items: center;
    }
    &.prize-draw {
      position: relative;
      overflow: hidden;
      min-height: 500px;
      background-position: center top;
      background-repeat: no-repeat;
      background-attachment: fixed;
      background-size: cover;
      h2 {
        ${goldGradientTextOutline}
        color: black;
        margin: 1rem;
      }
      .prize-types p {
        font-size: 46px;
        ${goldGradientTextOutline}
        line-height: 50px;
      }
      img {
        width: 110%;
        position: absolute;
        bottom: -5%;
        left: -5%;
        padding: 0;
        max-width: none;
      }
    }
  }
  @media (max-width: 768px) {
    &.red-envelopes {
      padding: 1.5rem 1.25rem;
    }
    &.red-envelopes h2 {
      font-size: 2.6rem;
      line-height: 1.1;
      margin-top: 0.75rem;
    }
    &.red-envelopes p {
      font-size: 0.95rem;
    }
    &.red-envelopes.prize-draw {
      padding-top: 2rem;
    }
    &.red-envelopes.prize-draw .prize-types p {
      font-size: 30px;
      line-height: 36px;
    }
    &.red-envelopes.prize-draw img {
      bottom: -8%;
      left: -8%;
      width: 120%;
    }
  }
  &.gala-announcement {
    max-width: 500px;
    margin: 1rem auto 2rem auto;
    padding: 1rem 2rem;
    text-align: center;
    p {
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
    .icon {
      font-size: 1.1rem;
      flex-shrink: 0;
    }
    a {
      color: #ffffff;
      text-decoration: underline;
      &:hover {
        color: #f2d28b;
      }
    }
  }
  &.prize-draw {
    position: relative;
    overflow: hidden;
    h2 {
      ${goldGradientTextOutline}
      color: black;
      margin: 1rem;
    }
    .prize-types p {
      font-size: 50px;
      ${goldGradientTextOutline}
      line-height: 55px;
    }
    img {
      width: 110%;
      position: absolute;
      bottom: -5%;
      left: -5%;
      padding: 0;
      max-width: none;
    }
  }
  ul {
    margin: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    max-width: 240px;
    li {
      margin: 0;
    }
  }
`;

const IntroContainer = styled.div`
  background-image: ${(props) => `url(${props.bgImage})`};
  background-color: #debc8b;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  position: relative;
  padding-bottom: 0 !important;
`;

const Divider = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  bottom: -42px;
  width: 80%;
  left: 10%;
  z-index: 5;
  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 3px;
    background-color: #6c5938;
  }

  img {
    max-width: 82px;
    max-height: 81px;
    margin: 0 15px;
  }
`;

const PlainContainer = styled.div`
  position: relative;
  background-color: #e4c69a;
  background-image: ${(props) => `url(${props.bgImage})`};
`;

const PatternWrap = styled.div`
  background-color: #e4c69a;
  background-image: ${(props) => `url(${props.bgImage})`};
  background-repeat: repeat;
  background-size: auto;

  ${PlainContainer} {
    background-color: transparent;
    background-image: none;
  }
`;

const HeaderBanner = styled.div`
  background: rgb(209, 148, 72);
  background: linear-gradient(
    0deg,
    rgba(209, 148, 72, 1) 0%,
    rgba(248, 226, 170, 1) 100%
  );
  padding: 8px;
  .inner {
    background-color: #b60000;
    background-image: ${(props) => `url(${props.bgImage})`};
    background-position: center top;
    background-repeat: no-repeat;
    background-attachment: fixed;
    background-size: cover;

    color: var(--white);
    padding: 1.5rem 1.5rem 1rem;
    text-align: center;
  }
  h1 {
    font-size: 2rem;
    margin: 0;
    text-transform: uppercase;
    color: var(--white);
    strong {
      font-size: 2.5rem;
      white-space: nowrap;
      color: var(--black);
      padding: 0 0.5rem;
      ${goldGradientTextOutline}

      @media (max-width: 768px) {
        font-size: 2rem;
        padding-top: 0.5rem;
        display: inline-block;
      }
    }
  }
`;

const MainContentDiv = styled.div`
  background-position: top;
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: cover;
  color: var(--lightblack);
  container-div {
    background: transparent;
    @media (max-width: 768px) {
      padding: 0;
    }
    > container-div {
      padding: 1em;
      background: #ffd289;
      @media (min-width: 768px) {
        padding: 2em;
      }
      @media (min-width: 991px) {
        padding: 3em;
      }
    }
  }

  .radius-mask {
    border-radius: 30px;
    overflow: hidden;
  }
  @media (min-width: 992px) {
    padding: 0 0 3rem 0;
  }
  height: auto;
  width: 100%;
  text-align: left;
`;

const CelebrationContainer = styled.div`
  background-image: ${(props) => `url(${props.bgImage})`};
  background-color: #debc8b;
  background-size: cover;
  background-repeat: repeat;
  background-position: top left;
  border-radius: 30px;
  padding: 2rem;
  position: relative;
  width: 100%;

  @media (min-width: 768px) {
    padding: 3rem;
  }

  h2 {
    color: #b60000;
    text-align: center;
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 2rem;
    text-transform: uppercase;
    letter-spacing: 1px;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
`;

const CelebrationSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const CelebrationItem = styled.div`
  padding: 1rem 2rem;
  border-radius: 10px;

  p {
    color: #333;
    line-height: 1.6;
    margin: 0.5rem 0 0 0;
    font-size: 1rem;
  }

  ul {
    margin: 1rem 0 0 0;
    padding-left: 1.5rem;

    li {
      color: #333;
      line-height: 1.8;
      margin-bottom: 0.5rem;
    }
  }
`;

const ItemTitle = styled.h3`
  color: #000;
  font-size: 1.25rem;
  font-weight: bold;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

export default ChineseNewYear2026;
